import { IAPPROPOSALSTATUS, SecurityAccessFeaturesEnum } from 'bff';
import { ProposalStatusGuard } from 'components/atoms/route-guard/ProposalStatusGuard';
import { RouteGuard } from 'components/atoms/route-guard/RouteGuard';
import { RouteBoundary } from 'components/organisms/route-boundary/RouteBoundary';
import { ProposalOverview } from 'modules/new-business/components/pages/overview/ProposalOverview';
import { EditProposalLayout } from 'modules/new-business/components/templates/proposals/EditProposalLayout';
import { NewProposalLayout } from 'modules/new-business/components/templates/proposals/NewProposalLayout';
import { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import { routes } from 'routes/route-paths';
import { StepHandle } from 'types/routing/proposal-step-handle';

const NewProposal = lazy(() => import('modules/new-business/components/pages/new/NewProposal'));
const ClientDetails = lazy(
  () => import('modules/new-business/components/pages/edit/client-details/ClientDetails')
);
const ProductSelection = lazy(
  () => import('modules/new-business/components/pages/edit/product-selection/ProductSelection')
);
const InvestmentDetails = lazy(
  () => import('modules/new-business/components/pages/edit/investment-details/InvestmentDetails')
);
const ProposalSummary = lazy(
  () => import('modules/new-business/components/pages/edit/proposal-summary/ProposalSummary')
);
const Application = lazy(
  () => import('modules/new-business/components/pages/edit/application/Application')
);
const PersonalDetails = lazy(
  () =>
    import(
      'modules/new-business/components/pages/edit/application/personal-details/PersonalDetails'
    )
);
const TaxDetails = lazy(
  () => import('modules/new-business/components/pages/edit/application/tax-details/TaxDetails')
);
const JustPortfolio = lazy(
  () =>
    import('modules/new-business/components/pages/edit/application/Just-portfolio/JustPortfolio')
);
const ThirdParties = lazy(
  () => import('modules/new-business/components/pages/edit/application/third-parties/ThirdParties')
);
const AppointIntermediary = lazy(
  () =>
    import(
      'modules/new-business/components/pages/edit/application/appoint-intermediary/AppointIntermediary'
    )
);
const PaymentMethods = lazy(
  () =>
    import('modules/new-business/components/pages/edit/application/payment-methods/PaymentMethods')
);
const Signing = lazy(() => import('modules/new-business/components/pages/edit/signing/Signing'));

const SigningSummary = lazy(
  () => import('modules/new-business/components/pages/edit/signing/SigningProgressSummary')
);

export const proposalRoutes: RouteObject[] = [
  {
    path: routes.proposalsRoutes.edit,
    element: (
      <RouteGuard
        component={
          <RouteBoundary>
            <EditProposalLayout />
          </RouteBoundary>
        }
        features={[
          SecurityAccessFeaturesEnum.EditProposal,
          SecurityAccessFeaturesEnum.CreateProposal,
          SecurityAccessFeaturesEnum.NewBusiness
        ]}
      />
    ),
    handle: {
      breadcrumb: { text: 'Edit proposal' }
    },
    children: [
      {
        path: routes.proposalsRoutes.editRoutes.clientDetails,
        handle: {
          stepNumber: 1
        } satisfies StepHandle,
        element: <ClientDetails />
      },
      {
        path: routes.proposalsRoutes.editRoutes.productSelection,
        handle: {
          stepNumber: 2
        } satisfies StepHandle,
        element: <ProductSelection />
      },
      {
        path: routes.proposalsRoutes.editRoutes.investmentDetails,
        handle: {
          stepNumber: 3
        } satisfies StepHandle,
        element: <InvestmentDetails />
      },
      {
        path: routes.proposalsRoutes.editRoutes.proposalSummary,
        handle: {
          stepNumber: 4
        } satisfies StepHandle,
        element: <ProposalSummary />
      },
      {
        path: routes.proposalsRoutes.editRoutes.application,
        handle: {
          stepNumber: 5
        } satisfies StepHandle,
        element: <Application />,
        children: [
          {
            index: true,
            element: (
              <Navigate to={routes.proposalsRoutes.editRoutes.applicationRoutes.personalDetails} />
            )
          },
          {
            path: routes.proposalsRoutes.editRoutes.applicationRoutes.personalDetails,
            element: <PersonalDetails />
          },
          {
            path: routes.proposalsRoutes.editRoutes.applicationRoutes.taxDetails,
            element: <TaxDetails />
          },
          {
            path: routes.proposalsRoutes.editRoutes.applicationRoutes.justPortfolio,
            element: <JustPortfolio />
          },
          {
            path: routes.proposalsRoutes.editRoutes.applicationRoutes.thirdParties,
            element: <ThirdParties />
          },
          {
            path: routes.proposalsRoutes.editRoutes.applicationRoutes.paymentMethods,
            element: <PaymentMethods />
          },
          {
            path: routes.proposalsRoutes.editRoutes.applicationRoutes.appointIntermediary,
            element: <AppointIntermediary />
          }
        ]
      },
      {
        path: routes.proposalsRoutes.editRoutes.signing,
        handle: {
          stepNumber: 6
        } satisfies StepHandle,
        element: (
          <ProposalStatusGuard
            component={<Signing />}
            backupRoute={routes.proposalsRoutes.editRoutes.signingSummary}
            requiredProposalStatus={[
              IAPPROPOSALSTATUS.ApplicationInProgress,
              IAPPROPOSALSTATUS.ApplicationComplete
            ]}
          />
        )
      },
      {
        path: routes.proposalsRoutes.editRoutes.signingSummary,
        handle: {
          stepNumber: 6
        } satisfies StepHandle,
        element: (
          <ProposalStatusGuard
            component={<SigningSummary />}
            backupRoute={routes.proposalsRoutes.editRoutes.signing}
            requiredProposalStatus={[
              IAPPROPOSALSTATUS.SigningComplete,
              IAPPROPOSALSTATUS.SigningInProgress
            ]}
          />
        )
      }
    ]
  },
  {
    path: routes.proposalsRoutes.new,
    handle: {
      breadcrumb: { text: 'New proposal' }
    },
    element: (
      <RouteGuard
        component={
          <RouteBoundary>
            <NewProposalLayout />
          </RouteBoundary>
        }
        features={[
          SecurityAccessFeaturesEnum.EditProposal,
          SecurityAccessFeaturesEnum.CreateProposal,
          SecurityAccessFeaturesEnum.NewBusiness
        ]}
      />
    ),
    children: [
      {
        index: true,
        element: <NewProposal />
      }
    ]
  },
  {
    index: true,
    element: (
      <RouteGuard
        component={
          <RouteBoundary>
            <ProposalOverview />
          </RouteBoundary>
        }
        features={[
          SecurityAccessFeaturesEnum.NewBusiness,
          SecurityAccessFeaturesEnum.ProposalOverview,
          SecurityAccessFeaturesEnum.DeleteProposal
        ]}
      />
    )
  }
];
